import { Alert, Anchor, Badge, Breadcrumbs, Button, Card, Menu, SegmentedControl, Select, SimpleGrid, Stack, Tabs, Text } from "@mantine/core";
import { IconAlertTriangle,
  IconSatellite,
  IconBasket, IconCalendarCheck, IconCalendarStats, IconCamera, IconClipboardCheck, IconCube, IconDroplet, IconFiles, IconLayoutDashboard, IconLeaf, IconMap2,
  IconPlant, IconPlus, IconSpray, IconTableImport, IconTool, IconTree, IconTrees,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { ReportButton } from "../../components/ReportButton";
import { fmtDate, fmtNum, KpiGrid, SEASONS } from "../../components/ui";
import { UnitHero } from "../../components/UnitHero";
import { useT } from "../../i18n/script";
import { DocumentsPanel } from "../dms/DmsPage";
import { speciesColor, useSpeciesNames } from "../holdings/HoldingsPage";
import { ImportPanel } from "../imports/ImportPage";
import MapPage from "../MapPage";
import { DiaryTab } from "./DiaryTab";
import { PlanTab } from "./PlanTab";
import { InspectionsTab } from "./InspectionsTab";
import { LotsTab } from "./LotsTab";
import { PhenologyTab } from "./PhenologyTab";
import SatelliteTab from "./SatelliteTab";
import { GrowthTab, TreesTab } from "./TreesTab";

export interface Orchard {
  id: number; name: string; species: string; cultivar?: string; rootstock?: string; planting_year?: number; training_system?: string;
  irrigation?: string; row_spacing_m?: number; tree_spacing_m?: number; density_per_ha?: number; producer?: string; bpg?: string;
  holding_id?: number; opstina?: string; opstina_code?: string; area_ha: number; tree_count: number; cadastral_ref?: string; status: string;
  organic: boolean; hail_net: boolean; hail_net_year?: number; pollinators?: string; altitude_m?: number; geometry: unknown;
  planting_material?: Record<string, string>; site_id?: number | null; species_all?: string[]; attributes?: Record<string, unknown>;
}

function Info({ label, value }: { label: string; value: unknown }) {
  const { t } = useT();
  return <div><Text size="xs" c="dimmed">{t(label)}</Text><Text size="sm" fw={500}>{value === null || value === undefined || value === "" ? "—" : String(value)}</Text></div>;
}

export default function OrchardDetailPage() {
  const { id } = useParams();
  const oid = Number(id);
  const { t } = useT();
  const can = useCan();
  const nav = useNavigate();
  const [season, setSeason] = useState(String(new Date().getFullYear()));
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "pregled";
  const sub = params.get("pod") ?? "";
  const setTab = (v: string) => { params.set("tab", v); params.delete("pod"); setParams(params, { replace: true }); };
  const setSub = (v: string) => { params.set("pod", v); setParams(params, { replace: true }); };
  const [openNew, setOpenNew] = useState<{ key: string; n: number } | null>(null);
  const [upload, setUpload] = useState(0);
  const [open3d, setOpen3d] = useState(false);
  const spName = useSpeciesNames();
  const pcs = useQuery({ queryKey: ["pointclouds", oid], enabled: can("pointcloud:read"),
    queryFn: () => http.get<{ status: string; has_viewer: boolean }[]>(`/api/v1/orchards/${oid}/pointclouds`) });
  const has3d = (pcs.data ?? []).some((p) => p.status === "ready" && p.has_viewer);
  const o = useQuery({ queryKey: ["orchard", oid], queryFn: () => http.get<Orchard>(`/api/v1/orchards/${oid}`), retry: false });
  const pred = useQuery({ queryKey: ["pred", oid, season], enabled: can("analytics:read"),
    queryFn: () => http.get<{ kind: string; value: number | null; value_date: string | null; low: number | null; high: number | null; model_version: string; metrics: Record<string, unknown> }[]>(
      "/api/v1/predictions", { orchard_id: oid, sezona: season }) });
  const spec = useQuery({ queryKey: ["vrsta_voca"], staleTime: 600_000,
    queryFn: () => http.get<{ code: string; attributes: Record<string, number> }[]>("/api/v1/codelists/vrsta_voca/items") });
  if (o.error) return <Alert color="red" icon={<IconAlertTriangle />} title={t("Voćnjak nije dostupan")}>{(o.error as Error).message}</Alert>;
  if (!o.data) return <Text>{t("Učitavanje...")}</Text>;
  const x = o.data;
  const norm = spec.data?.find((s) => s.code === x.species.toUpperCase())?.attributes ?? {};
  // Prije UAV detekcije stabla nisu registrovana pojedinačno — prikazuje se broj iz prijave (zahtjev za registraciju).
  const declared = Number(x.attributes?.broj_stabala_prijava) || 0;
  const trees = x.tree_count || declared;
  const density = trees && x.area_ha ? trees / x.area_ha : x.density_per_ha;
  const p = (k: string) => pred.data?.find((r) => r.kind === k);
  const py = p("prinos_t_ha"), pb = p("datum_cvjetanja"), ph = p("datum_berbe");
  const go = (t2: string, sub?: string) => { setTab(t2); if (sub) setSub(sub); };
  const request = (t2: string, sub: string | undefined, key: string) => { go(t2, sub); setOpenNew({ key, n: Date.now() }); };
  const entries: { label: string; perm: string; icon: ReactNode; run: () => void }[] = [
    { label: "Tretman (zaštita bilja)", perm: "diary:write", icon: <IconSpray size={16} />, run: () => request("knjiga", "operacije", "zastita") },
    { label: "Đubrenje", perm: "diary:write", icon: <IconPlant size={16} />, run: () => request("knjiga", "operacije", "dubrenje") },
    { label: "Navodnjavanje", perm: "diary:write", icon: <IconDroplet size={16} />, run: () => request("knjiga", "operacije", "navodnjavanje") },
    { label: "Druga radna operacija", perm: "diary:write", icon: <IconTool size={16} />, run: () => request("knjiga", "operacije", "rezidba") },
    { label: "Berba — nova partija", perm: "diary:write", icon: <IconBasket size={16} />, run: () => request("knjiga", "berba", "berba") },
    { label: "Fenološko zapažanje", perm: "phenology:write", icon: <IconLeaf size={16} />, run: () => go("fenologija") },
    { label: "Zapisnik o kontroli", perm: "inspections:write", icon: <IconClipboardCheck size={16} />, run: () => request("knjiga", "kontrole", "kontrola") },
    { label: "Fotografije stabla", perm: "photos:write", icon: <IconCamera size={16} />, run: () => go("stabla", "plodovi") },
    { label: "3D snimak (LAS/LAZ)", perm: "pointcloud:upload", icon: <IconCube size={16} />, run: () => { go("stabla", "metrika"); setUpload(Date.now()); } },
    { label: "Uvoz iz Excela / ZIP-a", perm: "data:import", icon: <IconTableImport size={16} />, run: () => go("uvoz") },
  ].filter((e) => can(e.perm));
  // dolazak sa „Upiši …“ iz uvoza: ?novi=zastita|dubrenje|berba|fenologija|fotografije|stabla|kontrola
  const NOVI: Record<string, () => void> = {
    zastita: () => request("knjiga", "operacije", "zastita"), dubrenje: () => request("knjiga", "operacije", "dubrenje"),
    berba: () => request("knjiga", "berba", "berba"), kontrola: () => request("knjiga", "kontrole", "kontrola"),
    fenologija: () => go("fenologija"), fotografije: () => go("stabla", "plodovi"), stabla: () => go("stabla"),
  };
  const novi = params.get("novi");
  if (novi && NOVI[novi]) {
    params.delete("novi");
    queueMicrotask(NOVI[novi]);
  }

  return (
    <Stack gap="md">
      <Breadcrumbs>
        <Anchor size="sm" onClick={() => nav("/gazdinstva")}>{t("Gazdinstva i voćnjaci")}</Anchor>
        {x.holding_id ? <Anchor size="sm" onClick={() => nav(`/gazdinstva/${x.holding_id}`)}>{x.producer}</Anchor> : <Text size="sm">—</Text>}
        <Text size="sm">{x.name}</Text>
      </Breadcrumbs>
      <UnitHero geojson={x.geometry} onMapClick={() => go("mapa")}
        subtitle={<>{x.producer ?? "—"} · BPG {x.bpg ?? "—"} · {t(x.opstina ?? "")}{x.cadastral_ref ? ` · ${x.cadastral_ref}` : ""}</>}
        title={x.name}
        badges={<>
          <Badge variant="white" style={{ color: speciesColor(x.species) }}>{t(spName(x.species))}{x.cultivar ? ` · ${x.cultivar}` : ""}</Badge>
          {x.organic && <Badge color="green" variant="filled">{t("organska")}</Badge>}
          {x.hail_net && <Badge color="gray" variant="filled">{t("protivgradna mreža")}</Badge>}
          {has3d && <Badge color="lime" variant="filled" leftSection={<IconCube size={11} />}>3D</Badge>}
        </>}
        stats={[{ label: "ha", value: fmtNum(x.area_ha, 2) }, { label: x.tree_count ? t("stabala") : declared ? t("stabala (prijava)") : t("stabala"), value: fmtNum(trees, 0) },
          { label: t("stabala/ha"), value: fmtNum(density, 0) }, { label: t("godina sadnje"), value: x.planting_year ?? "—" }]}
        actions={<>
          {entries.length > 0 && (
            <Menu position="bottom-start" width={260} shadow="md">
              <Menu.Target><Button size="xs" color="lime" leftSection={<IconPlus size={14} />}>{t("Novi unos")}</Button></Menu.Target>
              <Menu.Dropdown>
                {entries.map((e) => <Menu.Item key={e.label} leftSection={e.icon} onClick={e.run}>{t(e.label)}</Menu.Item>)}
              </Menu.Dropdown>
            </Menu>
          )}
          <Button size="xs" variant="white" color="dark" leftSection={<IconMap2 size={14} />} onClick={() => go("mapa")}>{t("Mapa")}</Button>
          {can("pointcloud:read") && has3d && <Button size="xs" variant="white" color="dark" leftSection={<IconCube size={14} />}
            onClick={() => { go("mapa"); setOpen3d(true); }}>3D</Button>}
          <Select size="xs" w={92} data={SEASONS} value={season} onChange={(v) => v && setSeason(v)} aria-label={t("Sezona")} />
          <ReportButton code="knjiga_vocnjaka" params={{ orchard_id: oid, sezona: Number(season) }} label="Knjiga (PDF)" size="xs" />
        </>} />

      <Tabs value={tab} onChange={(v) => v && setTab(v)} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="pregled" leftSection={<IconLayoutDashboard size={15} />}>{t("Pregled")}</Tabs.Tab>
          <Tabs.Tab value="mapa" leftSection={<IconMap2 size={15} />}>{t("Mapa i 3D")}</Tabs.Tab>
          <Tabs.Tab value="stabla" leftSection={<IconTree size={15} />}>{t("Stabla i plodovi")}</Tabs.Tab>
          {can("indices:read") && <Tabs.Tab value="satelit" leftSection={<IconSatellite size={15} />}>{t("Satelit")}</Tabs.Tab>}
          {can("phenology:read") && <Tabs.Tab value="fenologija" leftSection={<IconLeaf size={15} />}>{t("Fenologija")}</Tabs.Tab>}
          {can("diary:read") && <Tabs.Tab value="plan" leftSection={<IconCalendarCheck size={15} />}>{t("Plan radova")}</Tabs.Tab>}
          {can("diary:read") && <Tabs.Tab value="knjiga" leftSection={<IconCalendarStats size={15} />}>{t("Knjiga, berba i kontrole")}</Tabs.Tab>}
          {can("dms:read") && <Tabs.Tab value="dokumenti" leftSection={<IconFiles size={15} />}>{t("Upravljanje dokumentima")}</Tabs.Tab>}
          {can("data:import") && <Tabs.Tab value="uvoz" leftSection={<IconTableImport size={15} />}>{t("Uvoz")}</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="pregled" pt="md">
          <Stack>
            <KpiGrid items={[
              { label: x.tree_count ? "Stabala" : declared ? "Stabala (prijava)" : "Stabala", value: fmtNum(trees, 0), icon: <IconTrees />,
                hint: density ? `${fmtNum(density, 0)} ${t("stabala/ha")}${norm.min_stabala_ha ? ` · ${t("min.")} ${norm.min_stabala_ha}` : ""}` : undefined,
                color: norm.min_stabala_ha && density && density < norm.min_stabala_ha ? "red" : "agri", onClick: () => go("stabla", "metrika") },
              { label: `Predikcija prinosa ${season}`, value: py ? fmtNum(py.value, 1) : "—", unit: "t/ha", icon: <IconBasket />, color: "soil",
                hint: py ? `${fmtNum(py.low, 1)}–${fmtNum(py.high, 1)} t/ha · ${py.model_version}${norm.max_prinos_t_ha ? ` · ${t("normativ")} ${norm.max_prinos_t_ha}` : ""}` : undefined },
              { label: "Predviđeno cvjetanje", value: `${pb?.value_date ? fmtDate(pb.value_date) : "—"}`, icon: <IconLeaf />,
                hint: pb?.value != null ? <>{t("rizik od mraza")}: <b>{fmtNum(Number(pb.value) * 100, 0)}%</b></> : undefined, onClick: () => go("fenologija") },
              { label: "Predviđena berba", value: `${ph?.value_date ? fmtDate(ph.value_date) : "—"}`, icon: <IconBasket />, color: "soil",
                onClick: () => go("knjiga", "berba") },
            ]} />
            <Card withBorder>
              <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="md">
                <Info label="Vrsta" value={spName(x.species)} /><Info label="Sorta" value={x.cultivar} />
                <Info label="Podloga" value={x.rootstock} /><Info label="Godina sadnje" value={x.planting_year} />
                <Info label="Uzgojni oblik" value={x.training_system} /><Info label="Razmak (red × stablo)" value={x.row_spacing_m ? `${x.row_spacing_m} × ${x.tree_spacing_m} m` : null} />
                <Info label="Navodnjavanje" value={x.irrigation ?? "nema"} /><Info label="Protivgradna mreža" value={x.hail_net ? `da (${x.hail_net_year ?? "—"})` : "ne"} />
                <Info label="Oprašivači" value={x.pollinators} /><Info label="Nadmorska visina" value={x.altitude_m ? `${x.altitude_m} m` : null} />
                <Info label="Sadni materijal" value={x.planting_material ? Object.values(x.planting_material).join(", ") : null} />
                <Info label="Status" value={x.status} />
              </SimpleGrid>
            </Card>
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="mapa" pt="md">
          <MapPage height="calc(100dvh - 250px)" compact orchardId={oid} open3d={open3d} />
          <Text size="xs" c="dimmed" mt={6}>{t("Voćnjak je istaknut; slojevi stabala i parcela se uključuju u panelu slojeva. Dugme 3D u kartici voćnjaka otvara oblak tačaka, 2D vraća mapu.")}</Text>
        </Tabs.Panel>
        <Tabs.Panel value="stabla" pt="md">
          <SegmentedControl mb="md" value={sub === "plodovi" ? "plodovi" : "metrika"} onChange={setSub} data={[
            { value: "metrika", label: t(can("pointcloud:read") ? "Stabla, metrika i 3D snimci" : "Stabla i metrika") },
            { value: "plodovi", label: t("Fotografije i prirast ploda") }]} />
          {sub === "plodovi" ? <GrowthTab orchardId={oid} season={Number(season)} />
            : <TreesTab orchardId={oid} openUpload={upload} siteId={x.site_id} />}
        </Tabs.Panel>
        <Tabs.Panel value="satelit" pt="md"><SatelliteTab orchardId={oid} siteId={x.site_id} /></Tabs.Panel>
        <Tabs.Panel value="fenologija" pt="md"><PhenologyTab orchardId={oid} season={Number(season)} /></Tabs.Panel>
        <Tabs.Panel value="plan" pt="md"><PlanTab orchard={x} season={Number(season)} /></Tabs.Panel>
        <Tabs.Panel value="knjiga" pt="md">
          <SegmentedControl mb="md" value={["berba", "kontrole"].includes(sub) ? sub : "operacije"} onChange={setSub} data={[
            { value: "operacije", label: t("Radne operacije") }, { value: "berba", label: t("Berba, partije i sljedivost") },
            { value: "kontrole", label: t("Kontrole") }]} />
          {sub === "berba" ? <LotsTab orchard={x} season={Number(season)} openNew={openNew?.key === "berba" ? openNew : null} />
            : sub === "kontrole" ? <InspectionsTab orchard={x} openNew={openNew?.key === "kontrola" ? openNew : null} />
              : <DiaryTab orchard={x} season={Number(season)} openNew={openNew && !["berba", "kontrola"].includes(openNew.key) ? openNew : null} />}
        </Tabs.Panel>
        <Tabs.Panel value="dokumenti" pt="md"><DocumentsPanel holdingId={x.holding_id} entity={["orchard", oid]} /></Tabs.Panel>
        <Tabs.Panel value="uvoz" pt="md">
          <ImportPanel context={{ bpg: x.bpg ?? "", orchard: String(oid), orchard_id: oid }}
            only={["tretmani", "dubrenje", "berba", "fenologija", "stabla", "fotografije_zip"]} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
